import React, { useState } from "react";
import { Check, ArrowRight, ArrowLeft, Home, Banknote, TrendingUp, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ConsentCheckboxes from "@/components/ConsentCheckboxes";
import { getAttribution, trackLead } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEEDS = [
  { id: "assurance", label: "Assurance", icon: Home, desc: "Auto, habitation, santé, prévoyance…" },
  { id: "credit", label: "Crédit", icon: Banknote, desc: "Immobilier, rachat, pro, consommation" },
  { id: "placement", label: "Placement", icon: TrendingUp, desc: "Assurance-vie, PER, SCPI, épargne" },
];

const PROFILES = ["Particulier", "Professionnel", "Entreprise"];

export default function QuoteForm({ defaultNeeds = [], page }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    needs: defaultNeeds,
    profile: "Particulier",
    project_details: "",
    amount: "",
    timeline: "",
    full_name: "",
    email: "",
    phone: "",
    consent_request: false,
    consent_marketing_phone: false,
    consent_marketing_email: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const toggleNeed = (id) => {
    setData((d) => ({
      ...d,
      needs: d.needs.includes(id) ? d.needs.filter((n) => n !== id) : [...d.needs, id],
    }));
  };

  const canNext = () => {
    if (step === 1) return data.needs.length > 0 && data.profile;
    if (step === 2) return data.project_details.length > 5;
    return true;
  };

  const submit = async () => {
    if (!data.full_name || !data.email) {
      toast({ title: "Champs requis", description: "Merci de renseigner votre nom et votre email.", variant: "destructive" });
      return;
    }
    if (data.consent_request && !data.phone.trim()) {
      toast({ title: "Téléphone requis", description: "Indiquez votre numéro pour être rappelé, ou décochez la case de rappel.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, source: getAttribution(page) }),
      });
      if (!res.ok) throw new Error("Erreur serveur");
      trackLead();
      setSubmitted(true);
      toast({ title: "Demande envoyée ✨", description: data.consent_request ? "Maël vous rappelle sous 24h ouvrées." : "Maël vous répond par email sous 24h ouvrées." });
    } catch (e) {
      toast({ title: "Une erreur est survenue", description: "Merci de réessayer ou de nous appeler.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="devis" data-testid="quote-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-32">
          <div className="text-xs uppercase tracking-[0.25em] text-md-accent font-bold mb-4">Demande de devis</div>
          <h2 className="font-serif text-4xl md:text-5xl text-md-primary font-medium leading-tight mb-6">
            Parlez-moi de <span className="italic">votre projet.</span>
          </h2>
          <p className="text-md-muted leading-relaxed mb-8">
            Un formulaire rapide en 3 étapes. Réponse personnalisée sous 24h ouvrées, sans aucun engagement.
          </p>
          <ul className="space-y-3 text-sm text-md-text">
            {["Devis gratuit & sans engagement", "Confidentiel — RGPD compliant", "Réponse sous 24h ouvrées"].map((p) => (
              <li key={p} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-md-secondary" /> {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-8">
          <div className="bg-md-primary rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-md-secondary/15 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-md-accent/15 rounded-full blur-3xl" />

            {!submitted ? (
              <div className="relative">
                <div className="flex items-center gap-3 mb-10">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className="flex items-center gap-3">
                      <div
                        data-testid={`quote-step-indicator-${s}`}
                        className={`w-9 h-9 rounded-full grid place-items-center text-sm font-semibold transition-all ${
                          step >= s ? "bg-md-secondary text-white" : "bg-white/10 text-white/50"
                        }`}
                      >
                        {step > s ? <Check className="w-4 h-4" /> : s}
                      </div>
                      {s < 3 && <div className={`w-12 h-px ${step > s ? "bg-md-secondary" : "bg-white/15"}`} />}
                    </div>
                  ))}
                  <div className="ml-auto text-xs uppercase tracking-widest text-white/60">Étape {step} / 3</div>
                </div>

                {step === 1 && (
                  <div data-testid="quote-step-1" className="space-y-7 animate-fade-up">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-md-secondary mb-4 block">Quels sont vos besoins ?</label>
                      <div className="grid sm:grid-cols-3 gap-3">
                        {NEEDS.map((n) => {
                          const I = n.icon;
                          const active = data.needs.includes(n.id);
                          return (
                            <button
                              key={n.id}
                              type="button"
                              data-testid={`quote-need-${n.id}`}
                              onClick={() => toggleNeed(n.id)}
                              className={`text-left p-5 rounded-2xl border-2 transition-all ${
                                active
                                  ? "border-md-secondary bg-md-secondary/15"
                                  : "border-white/15 hover:border-white/40"
                              }`}
                            >
                              <I className="w-6 h-6 mb-3 text-md-secondary" />
                              <div className="font-semibold mb-1">{n.label}</div>
                              <div className="text-xs text-white/60">{n.desc}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs uppercase tracking-widest text-md-secondary mb-4 block">Vous êtes</label>
                      <div className="flex flex-wrap gap-2">
                        {PROFILES.map((p) => (
                          <button
                            key={p}
                            type="button"
                            data-testid={`quote-profile-${p.toLowerCase()}`}
                            onClick={() => setData((d) => ({ ...d, profile: p }))}
                            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                              data.profile === p
                                ? "bg-md-secondary text-white"
                                : "bg-white/5 text-white/80 hover:bg-white/10"
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="quote-step-2" className="space-y-6 animate-fade-up">
                    <Field label="Décrivez votre projet en quelques mots">
                      <textarea
                        data-testid="quote-project-details"
                        rows={4}
                        value={data.project_details}
                        onChange={(e) => setData({ ...data, project_details: e.target.value })}
                        placeholder="Ex : J'envisage un achat immobilier de 350 000€, primo-accédant…"
                        className="w-full bg-white/10 border border-white/20 rounded-xl p-4 text-white placeholder:text-white/40 focus:outline-none focus:border-md-secondary"
                      />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Montant approximatif (optionnel)">
                        <input
                          data-testid="quote-amount"
                          value={data.amount}
                          onChange={(e) => setData({ ...data, amount: e.target.value })}
                          placeholder="Ex : 250 000 €"
                          className="w-full bg-white/10 border border-white/20 rounded-xl p-4 text-white placeholder:text-white/40 focus:outline-none focus:border-md-secondary"
                        />
                      </Field>
                      <Field label="Échéance souhaitée">
                        <input
                          data-testid="quote-timeline"
                          value={data.timeline}
                          onChange={(e) => setData({ ...data, timeline: e.target.value })}
                          placeholder="Ex : Sous 3 mois"
                          className="w-full bg-white/10 border border-white/20 rounded-xl p-4 text-white placeholder:text-white/40 focus:outline-none focus:border-md-secondary"
                        />
                      </Field>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="quote-step-3" className="space-y-5 animate-fade-up">
                    <Field label="Nom complet *">
                      <input
                        data-testid="quote-name"
                        value={data.full_name}
                        onChange={(e) => setData({ ...data, full_name: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl p-4 text-white placeholder:text-white/40 focus:outline-none focus:border-md-secondary"
                        placeholder="Jean Dupont"
                      />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Email *">
                        <input
                          data-testid="quote-email"
                          type="email"
                          value={data.email}
                          onChange={(e) => setData({ ...data, email: e.target.value })}
                          className="w-full bg-white/10 border border-white/20 rounded-xl p-4 text-white placeholder:text-white/40 focus:outline-none focus:border-md-secondary"
                          placeholder="jean@email.com"
                        />
                      </Field>
                      <Field label={data.consent_request ? "Téléphone *" : "Téléphone (si rappel souhaité)"}>
                        <input
                          data-testid="quote-phone"
                          type="tel"
                          value={data.phone}
                          onChange={(e) => setData({ ...data, phone: e.target.value })}
                          className="w-full bg-white/10 border border-white/20 rounded-xl p-4 text-white placeholder:text-white/40 focus:outline-none focus:border-md-secondary"
                          placeholder="06 12 34 56 78"
                        />
                      </Field>
                    </div>
                    <ConsentCheckboxes
                      source="devis"
                      dark
                      testPrefix="quote"
                      value={{ request: data.consent_request, marketing_phone: data.consent_marketing_phone, marketing_email: data.consent_marketing_email }}
                      onChange={(v) => setData({ ...data, consent_request: v.request, consent_marketing_phone: v.marketing_phone, consent_marketing_email: v.marketing_email })}
                    />
                  </div>
                )}

                <div className="mt-10 flex items-center justify-between">
                  {step > 1 ? (
                    <button
                      type="button"
                      data-testid="quote-prev-btn"
                      onClick={() => setStep((s) => s - 1)}
                      className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm"
                    >
                      <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                  ) : <span />}

                  {step < 3 ? (
                    <button
                      type="button"
                      data-testid="quote-next-btn"
                      disabled={!canNext()}
                      onClick={() => setStep((s) => s + 1)}
                      className="inline-flex items-center gap-2 bg-md-secondary hover:bg-md-secondaryHover disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 rounded-full font-semibold transition-all"
                    >
                      Continuer <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="quote-submit-btn"
                      disabled={submitting}
                      onClick={submit}
                      className="inline-flex items-center gap-2 bg-md-secondary hover:bg-md-secondaryHover disabled:opacity-50 text-white px-6 py-3 rounded-full font-semibold transition-all"
                    >
                      {submitting ? "Envoi…" : "Envoyer ma demande"} <Send className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div data-testid="quote-success" className="relative text-center py-10">
                <div className="w-16 h-16 mx-auto rounded-full bg-md-secondary grid place-items-center mb-6">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-serif text-3xl mb-3">Merci, votre demande est bien reçue.</h3>
                <p className="text-white/80 max-w-md mx-auto">{data.consent_request ? "Maël vous rappellera personnellement sous 24h ouvrées au numéro indiqué." : "Maël vous répondra personnellement par email sous 24h ouvrées. Il ne vous appellera pas."}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-md-secondary mb-2 block">{label}</span>
      {children}
    </label>
  );
}
